/**
 * Singleton Google Identity Services (GIS) manager.
 *
 * Uses `google.accounts.id.renderButton()` instead of `prompt()` because
 * prompt() has an aggressive browser-level cooldown after dismissal and
 * silently no-ops, making it unreliable for button-click flows.
 *
 * The rendered button is placed inside a container element provided by the
 * consumer. Clicking the Google button opens the full sign-in popup and
 * returns the JWT ID token via the callback.
 */

import { isNativeContext } from "@/lib/native-bridge";

const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ?? "";
const GOOGLE_ALLOWED_ORIGINS = (process.env.NEXT_PUBLIC_GOOGLE_ALLOWED_ORIGINS ?? "")
  .split(",")
  .map((origin) => normalizeOrigin(origin))
  .filter(Boolean);
const GOOGLE_ALLOWED_ORIGIN_SET = new Set(GOOGLE_ALLOWED_ORIGINS);

type CredentialCallback = (credential: string) => void;

export type GoogleSignInUnavailableReason =
  | "missing_client_id"
  | "native_context"
  | "https_required"
  | "ip_origin_not_supported"
  | "origin_not_allowed";

export interface GoogleSignInAvailability {
  enabled: boolean;
  reason: GoogleSignInUnavailableReason | null;
  origin: string | null;
}

let initialized = false;
let currentCallback: CredentialCallback | null = null;

function normalizeOrigin(origin: string): string {
  return origin.trim().toLowerCase().replace(/\/+$/, "");
}

function isLoopbackHost(host: string): boolean {
  return host === "localhost" || host === "127.0.0.1" || host === "::1" || host === "[::1]";
}

function isIPv4Host(host: string): boolean {
  const parts = host.split(".");
  if (parts.length !== 4) return false;
  return parts.every((part) => {
    if (!/^\d+$/.test(part)) return false;
    const n = Number(part);
    return n >= 0 && n <= 255;
  });
}

function isIPv6Host(host: string): boolean {
  return host.includes(":");
}

function isIpHost(host: string): boolean {
  return isIPv4Host(host) || isIPv6Host(host);
}

function evaluateOrigin(origin: string): GoogleSignInAvailability {
  let parsed: URL;
  try {
    parsed = new URL(origin);
  } catch {
    return { enabled: false, reason: "origin_not_allowed", origin: null };
  }

  const normalizedOrigin = normalizeOrigin(parsed.origin);
  const host = parsed.hostname.toLowerCase();
  const loopback = isLoopbackHost(host);

  if (!loopback && isIpHost(host)) {
    return {
      enabled: false,
      reason: "ip_origin_not_supported",
      origin: normalizedOrigin,
    };
  }

  if (!loopback && parsed.protocol !== "https:") {
    return {
      enabled: false,
      reason: "https_required",
      origin: normalizedOrigin,
    };
  }

  if (GOOGLE_ALLOWED_ORIGINS.length === 0) {
    return loopback
      ? { enabled: true, reason: null, origin: normalizedOrigin }
      : { enabled: false, reason: "origin_not_allowed", origin: normalizedOrigin };
  }

  if (!GOOGLE_ALLOWED_ORIGIN_SET.has(normalizedOrigin)) {
    return {
      enabled: false,
      reason: "origin_not_allowed",
      origin: normalizedOrigin,
    };
  }

  return { enabled: true, reason: null, origin: normalizedOrigin };
}

export function getGoogleSignInAvailability(): GoogleSignInAvailability {
  if (!GOOGLE_CLIENT_ID) {
    return {
      enabled: false,
      reason: "missing_client_id",
      origin: typeof window !== "undefined" ? normalizeOrigin(window.location.origin) : null,
    };
  }

  if (typeof window === "undefined") {
    return {
      enabled: false,
      reason: null,
      origin: null,
    };
  }

  if (isNativeContext()) {
    return {
      enabled: false,
      reason: "native_context",
      origin: normalizeOrigin(window.location.origin),
    };
  }

  return evaluateOrigin(window.location.origin);
}

/** Initialize GIS once. Safe to call multiple times — subsequent calls are no-ops. */
export function initGoogleSignIn() {
  const availability = getGoogleSignInAvailability();
  if (
    typeof window === "undefined" ||
    initialized ||
    !availability.enabled ||
    !window.google?.accounts?.id
  ) {
    return;
  }

  window.google.accounts.id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: (response: { credential: string }) => {
      currentCallback?.(response.credential);
    },
    auto_select: false,
    cancel_on_tap_outside: true,
  });

  initialized = true;
}

/** Set the callback that will receive the JWT credential on next sign-in. */
export function setGoogleCallback(cb: CredentialCallback) {
  currentCallback = cb;
}

/** Render the Google Sign-In button inside a container element. */
export function renderGoogleButton(
  container: HTMLElement,
  colorScheme: "light" | "dark" = "light",
) {
  if (typeof window === "undefined") return;
  if (!getGoogleSignInAvailability().enabled || !window.google?.accounts?.id) return;
  initGoogleSignIn();

  // Clear previous render (theme may have changed)
  container.innerHTML = "";

  // Render at full container width. The iframe stretches to fill.
  const width = Math.max(container.offsetWidth, 200);

  window.google.accounts.id.renderButton(container, {
    type: "standard",
    size: "large",
    theme: colorScheme === "dark" ? "filled_black" : "outline",
    text: "continue_with",
    shape: "rectangular",
    logo_alignment: "center",
    width,
  });
}

/** Check if the GIS script is loaded. */
export function isGoogleReady(): boolean {
  if (typeof window === "undefined") return false;
  if (!getGoogleSignInAvailability().enabled) return false;
  return !!window.google?.accounts?.id;
}
